import { createContext, useCallback, useContext, useMemo } from 'react';
import { translate as translateText } from './format';
import type {
  TranslateFn,
  TranslationProviderProps,
  TranslationWarningHandler,
} from './types';

export interface TranslationContextValue<Texts = unknown> {
  texts: Texts;
  translate: TranslateFn;
}

export const TranslationContext = createContext<
  TranslationContextValue | undefined
>(undefined);

export function TranslationProvider<Texts>({
  children,
  texts,
  showKeys = false,
  onWarning,
}: TranslationProviderProps<Texts>) {
  const translate = useRuntimeTranslate(showKeys, onWarning);

  const value = useMemo<TranslationContextValue<Texts>>(
    () => ({
      texts,
      translate,
    }),
    [texts, translate]
  );

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation<Texts = unknown>() {
  const context = useContext(TranslationContext);

  if (context === undefined) {
    throw new Error('useTranslation must be used within TranslationProvider');
  }

  return context as TranslationContextValue<Texts>;
}

export function useTexts<Texts = unknown>() {
  return useTranslation<Texts>().texts;
}

function useRuntimeTranslate(
  showKeys: boolean,
  onWarning: TranslationWarningHandler | undefined
) {
  return useCallback<TranslateFn>(
    (text, options) =>
      translateText(text, {
        ...options,
        showKeys,
        onWarning,
      }),
    [showKeys, onWarning]
  );
}
